import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  DialogContent,
  Divider,
  IconButton,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { differenceInDays, startOfDay } from 'date-fns';
import { PurchaseOrderItem } from '@domain/purchasing';
import { Shipment } from '@domain/shipment';
import { usePurchaseOrderItems } from '@features/purchasing/hooks';
import { PurchaseOrderShipment } from '@features/purchasing/components/shipment/PurchaseOrderShipment';
import {
  LeadTimeTimeline,
  ProductDisplay,
} from '@features/shipments/components/shared';
import { TransportMethodIcon } from '@features/shipments/components/shared/TransportMethodIcon';
import { useShipments, useShipment } from '@features/shipments/hooks/useShipments';
import EditableDateCell from '@shared/ui/common/EditableDateCell';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { BottomSheet } from '@shared/shell/bottomsheet/BottomSheet';
import { Icon, IconSize } from '@shared/icons';

interface UpdatePurchaseOrderItemDatesProps {
  item: PurchaseOrderItem;
  onClose: () => void;
}

const sameDay = (a?: Date | null, b?: Date | null): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return startOfDay(a).getTime() === startOfDay(b).getTime();
};

const UpdatePurchaseOrderItemDates: React.FC<
  UpdatePurchaseOrderItemDatesProps
> = ({ item: initialItem, onClose }) => {
  const theme = useTheme();
  const { updatePurchaseOrderItem } = usePurchaseOrderItems();
  const { updateShipment } = useShipments();
  const [item, setItem] = useState(initialItem);
  const [baseline, setBaseline] = useState(initialItem);
  const [shipmentSheetOpen, setShipmentSheetOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const hydratedShipmentIdRef = useRef<string | null>(null);
  const { shipment: fullShipment } = useShipment(item.shipment?.id || '');

  useEffect(() => {
    setItem(initialItem);
    setBaseline(initialItem);
    hydratedShipmentIdRef.current = null;
  }, [initialItem]);

  useEffect(() => {
    if (!fullShipment?.id) return;
    if (hydratedShipmentIdRef.current === fullShipment.id) return;
    hydratedShipmentIdRef.current = fullShipment.id;

    setItem((current) => {
      if (!current.shipment || current.shipment.id !== fullShipment.id) {
        return current;
      }
      return {
        ...current,
        shipment: { ...fullShipment, ...current.shipment },
      };
    });
    setBaseline((current) => {
      if (!current.shipment || current.shipment.id !== fullShipment.id) {
        return current;
      }
      return {
        ...current,
        shipment: fullShipment,
      };
    });
  }, [fullShipment]);

  const shipmentItems =
    fullShipment?.purchaseOrderItems?.length
      ? fullShipment.purchaseOrderItems
      : item.shipment?.purchaseOrderItems?.length
        ? item.shipment.purchaseOrderItems
        : item.shipment
          ? [item]
          : [];

  const activeShipment = useMemo(() => {
    if (!item.shipment) return undefined;
    return {
      ...(fullShipment ?? item.shipment),
      ...item.shipment,
    } as Shipment;
  }, [fullShipment, item.shipment]);

  const patchItemDates = (patch: {
    productionStartDate?: Date;
    productionReadyDate?: Date;
    eta?: Date;
  }) => {
    setItem((current) => ({ ...current, ...patch }));
  };

  const patchShipmentDates = (patch: {
    departureDate?: Date;
    arrivalDate?: Date;
    processedDate?: Date;
  }) => {
    setItem((current) =>
      current.shipment
        ? { ...current, shipment: { ...current.shipment, ...patch } }
        : current
    );
  };

  const handleShipmentFinish = (shipment: Shipment) => {
    setItem((current) => ({ ...current, shipment }));
    setBaseline((current) => ({ ...current, shipment }));
    hydratedShipmentIdRef.current = shipment.id ?? null;
    setShipmentSheetOpen(false);
  };

  const handleSave = async () => {
    if (!item.id) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      const itemPatch: {
        productionStartDate?: Date;
        productionReadyDate?: Date;
        eta?: Date;
      } = {};

      if (!sameDay(item.productionStartDate, baseline.productionStartDate)) {
        itemPatch.productionStartDate = item.productionStartDate;
      }
      if (!sameDay(item.productionReadyDate, baseline.productionReadyDate)) {
        itemPatch.productionReadyDate = item.productionReadyDate;
      }
      if (!sameDay(item.eta, baseline.eta)) {
        itemPatch.eta = item.eta;
      }

      if (Object.keys(itemPatch).length > 0) {
        await updatePurchaseOrderItem({
          itemId: item.id,
          orderId: item.purchaseOrder?.id,
          ...itemPatch,
        });
      }

      const shipmentId = item.shipment?.id;
      if (shipmentId && item.shipment) {
        const shipmentPatch: {
          departureDate?: Date;
          arrivalDate?: Date;
          processedDate?: Date;
        } = {};

        if (
          !sameDay(
            item.shipment.departureDate,
            baseline.shipment?.departureDate
          )
        ) {
          shipmentPatch.departureDate = item.shipment.departureDate;
        }
        if (
          !sameDay(item.shipment.arrivalDate, baseline.shipment?.arrivalDate)
        ) {
          shipmentPatch.arrivalDate = item.shipment.arrivalDate;
        }
        if (
          !sameDay(
            item.shipment.processedDate,
            baseline.shipment?.processedDate
          )
        ) {
          shipmentPatch.processedDate = item.shipment.processedDate;
        }

        if (Object.keys(shipmentPatch).length > 0) {
          await updateShipment({
            id: shipmentId,
            request: shipmentPatch,
            purchaseOrderId: item.purchaseOrder?.id,
          });
        }
      }

      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          width="100%"
        >
          <Typography variant="h6" flexGrow={1}>
            Update dates
          </Typography>
          <IconButton onClick={onClose} aria-label="Close" disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogHeader>

      <DialogContent
        sx={{ p: 4, gap: 4, display: 'flex', flexDirection: 'column' }}
      >
        <Stack gap={2}>
          <Typography variant="subtitle2" color="text.primary">
            Product
          </Typography>
          <ProductDisplay purchaseOrderItems={[item]} />
        </Stack>

        <Divider />

        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 2,
            bgcolor: theme.palette.background.default,
          }}
        >
          <Typography variant="subtitle2" mb={2}>
            Production Schedule
          </Typography>
          <List disablePadding>
            <ListItem divider sx={{ justifyContent: 'space-between', px: 0 }}>
              <Typography variant="body2" color="text.primary">
                Production Start Date
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                gap={1}
              >
                {item.purchaseOrder?.orderDate && item.productionStartDate && (
                  <Typography variant="body2" color="text.secondary">
                    +
                    {differenceInDays(
                      startOfDay(item.productionStartDate),
                      startOfDay(item.purchaseOrder.orderDate)
                    )}{' '}
                    days
                  </Typography>
                )}
                <EditableDateCell
                  value={item.productionStartDate}
                  onValueChange={(date) => {
                    if (!date) return;
                    patchItemDates({ productionStartDate: date });
                  }}
                  maxDate={item.productionReadyDate}
                />
              </Stack>
            </ListItem>
            <ListItem sx={{ justifyContent: 'space-between', px: 0 }}>
              <Typography variant="body2" color="text.primary">
                Production Ready Date
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                gap={1}
              >
                {item.productionStartDate && item.productionReadyDate && (
                  <Typography variant="body2" color="text.secondary">
                    +
                    {differenceInDays(
                      startOfDay(item.productionReadyDate),
                      startOfDay(item.productionStartDate)
                    )}{' '}
                    days
                  </Typography>
                )}
                <EditableDateCell
                  value={item.productionReadyDate}
                  onValueChange={(date) => {
                    if (!date) return;
                    patchItemDates({ productionReadyDate: date });
                  }}
                  minDate={item.productionStartDate}
                />
              </Stack>
            </ListItem>
          </List>
        </Box>

        <Divider />

        {activeShipment ? (
          <Stack gap={3}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <TransportMethodIcon
                  size={40}
                  transportMethod={activeShipment.transportMethod}
                />
                <Stack spacing={0}>
                  <Typography variant="subtitle2" color="text.primary">
                    {activeShipment.name || 'Shipment'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {shipmentItems.length} product
                    {shipmentItems.length !== 1 ? 's' : ''} in shipment
                  </Typography>
                </Stack>
              </Stack>

              <Button
                color="secondary"
                onClick={() => setShipmentSheetOpen(true)}
                disabled={!item.purchaseOrder || saving}
                endIcon={
                  <Icon icon="ArrowRefresh02" size={IconSize.SMALL} />
                }
              >
                Change shipment
              </Button>
            </Stack>

            <Stack gap={2}>
              <Typography variant="subtitle2" color="text.primary">
                Products in shipment
              </Typography>
              <ProductDisplay purchaseOrderItems={shipmentItems} />
            </Stack>

            <LeadTimeTimeline
              departureDate={activeShipment.departureDate || new Date()}
              arrivalDate={activeShipment.arrivalDate || new Date()}
              processedDate={activeShipment.processedDate || new Date()}
              onDepartureDateChange={(date) =>
                patchShipmentDates({ departureDate: date })
              }
              onArrivalDateChange={(date) =>
                patchShipmentDates({ arrivalDate: date })
              }
              onProcessedDateChange={(date) =>
                patchShipmentDates({ processedDate: date })
              }
            />
          </Stack>
        ) : (
          <Stack gap={3}>
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 2,
                bgcolor: theme.palette.background.default,
              }}
            >
              <Typography variant="subtitle2" mb={2}>
                Estimated arrival
              </Typography>
              <List disablePadding>
                <ListItem sx={{ justifyContent: 'space-between', px: 0 }}>
                  <Typography variant="body2" color="text.primary">
                    ETA
                  </Typography>
                  <EditableDateCell
                    value={item.eta}
                    onValueChange={(date) => {
                      if (!date) return;
                      patchItemDates({ eta: date });
                    }}
                    minDate={item.productionReadyDate}
                  />
                </ListItem>
              </List>
            </Box>

            <Button
              variant="contained"
              onClick={() => setShipmentSheetOpen(true)}
              disabled={!item.purchaseOrder || saving}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add shipment
            </Button>
          </Stack>
        )}
      </DialogContent>

      <Box
        sx={{
          py: 3,
          px: 4,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            Save
          </Button>
        </Stack>
      </Box>

      {item.purchaseOrder && (
        <BottomSheet
          open={shipmentSheetOpen}
          onClose={() => setShipmentSheetOpen(false)}
        >
          <PurchaseOrderShipment
            handleClose={() => setShipmentSheetOpen(false)}
            handleFinish={handleShipmentFinish}
            purchaseOrder={item.purchaseOrder}
            selectedShipment={item.shipment || undefined}
            purchaseOrderItems={[item]}
            products={item.product ? [item.product] : []}
          />
        </BottomSheet>
      )}
    </>
  );
};

export default UpdatePurchaseOrderItemDates;
